import type { HTMLElementEvent } from "@simplybusiness/mobius";
import { Icon, Radio, RadioGroup, TextOrHTML } from "@simplybusiness/mobius";
import { useState } from "react";

import { iconMap, isValidIconKey } from ".";
import { DEFAULT_ADDRESS_SYNC_CONFIG } from "../../../../../../../constants/addressSyncDefaults";
import { useAddressSyncConfig } from "../../../../../../../features/questionnaire";
import { useEnvironmentValues } from "../../../../../../../hooks/environment";
import { useIsClient } from "@simplybusiness/mobius-hooks";
import type { QuestionComponentProps } from "../../types";
import { getOrientation } from "./orientation";

export const RadioButtonsQuestion = ({
  options,
  questionFieldProps,
  setAddressMode,
}: QuestionComponentProps) => {
  const {
    value,
    isDisabled = false,
    questionLabelProps,
    helpInfo,
    ...otherProps
  } = questionFieldProps;
  const [errorMessage, _] = useState<string | undefined>(undefined);
  const isClient = useIsClient();
  const { site } = useEnvironmentValues();
  const addressSyncConfig = useAddressSyncConfig();

  if (!options?.length) {
    console.warn(`No required options found`);
    return null;
  }

  const handleChange = (event: HTMLElementEvent<HTMLInputElement>) => {
    // Only toggle when changing from "no" to "yes" to avoid UI flashing
    const { value, name } = event.target;

    const {
      linkQuestionName,
      linkQuestionValue = "yes",
      linkedQuestionName,
    } = addressSyncConfig ?? DEFAULT_ADDRESS_SYNC_CONFIG;

    if (name === linkQuestionName) {
      setAddressMode?.(
        linkedQuestionName,
        value === linkQuestionValue ? "none" : "lookup",
      );
    }

    // Call factory handler
    questionFieldProps.onChange?.(event);
  };

  // Set radio group orientation
  const orientation = getOrientation({ site, optionsCount: options.length });

  return (
    <RadioGroup
      {...otherProps}
      isDisabled={!isClient || isDisabled}
      value={value as string}
      onChange={handleChange}
      errorMessage={errorMessage || questionFieldProps.errorMessage}
      className="mobius-radio-buttons-question"
      orientation={orientation as "horizontal" | "vertical"}
      autoStack={true}
    >
      {options.map(({ image, label, value }) => {
        if (!image || !isValidIconKey(image)) {
          if (image && !isValidIconKey(image)) {
            console.warn(
              `Invalid icon name provided to the radio button labelled "${label}"`,
            );
          }
          return (
            <Radio
              key={value}
              label={<TextOrHTML text={label} />}
              value={value}
            />
          );
        }

        return (
          <Radio key={value} value={value}>
            <Icon
              icon={iconMap[image]}
              size="sm"
              title={`Business location type: ${label}`}
            />
            <TextOrHTML text={label} />
          </Radio>
        );
      })}
    </RadioGroup>
  );
};
